import { hsIntersectRayWithLevel, type HsScreenRay } from './measurement-ray.function';
import type { HsMeasurementPoint } from './measurement.function';

/**
 * Where measurement clicks land: `auto` on the drawn surface under the cursor, `selected-node` on surfaces of the
 * selected scene node only, `plane` on the horizontal level of the first point (for horizontal distances and plan
 * areas).
 */
export type HsMeasurementSnapMode = 'auto' | 'selected-node' | 'plane';

/** How a point was placed: on a drawn surface or on the level of the measurement's first point. */
export type HsMeasurementPointSource = 'snap' | 'plane';

/** The drawn surface under the cursor, with the scene node it belongs to, if any (photogrammetry has none). */
export interface HsMeasurementHit {
  point: HsMeasurementPoint;
  nodeId: string | null;
}

/** A placed measurement point. */
export interface HsMeasurementPlacement {
  point: HsMeasurementPoint;
  source: HsMeasurementPointSource;
}

/**
 * Where a click places a measurement point, or null when it places none. The first point needs a surface: the
 * level of later points comes from it. A fixed level such as y = 0 would put points off the model hundreds of
 * meters away from models placed at their survey elevation, and inflate every value measured with them.
 */
export function hsResolveMeasurementPoint(
  snapMode: HsMeasurementSnapMode,
  hit: HsMeasurementHit | null,
  ray: HsScreenRay | null,
  options: { level: number | null; selectedNodeId: string | null }
): HsMeasurementPlacement | null {
  if (snapMode === 'selected-node') {
    return hit !== null && hit.nodeId !== null && hit.nodeId === options.selectedNodeId
      ? { point: hit.point, source: 'snap' }
      : null;
  }
  if (options.level === null || (snapMode === 'auto' && hit !== null)) {
    return hit === null ? null : { point: hit.point, source: 'snap' };
  }
  const point: HsMeasurementPoint | null = ray === null ? null : hsIntersectRayWithLevel(ray, options.level);
  return point === null ? null : { point, source: 'plane' };
}
